import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateNL, formatEUR } from '@/lib/format';
import { contractStatus } from '@/lib/contract';
import { ChevronLeft, Save, Trash2, User as UserIcon, ShieldCheck, FileText, CalendarDays, FileSignature, Plus, Download, Pencil, Loader2, Briefcase, LogIn, LogOut, RotateCcw, Stethoscope, CheckCircle2, FileDown, QrCode, X, Printer, AlertTriangle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import DocumentenSection from '@/components/DocumentenSection';
import { VerlengDialog } from '@/components/VerlengDialog';

const TABS = [
  { id: 'gegevens', label: 'Gegevens', icon: UserIcon },
  { id: 'dienstverband', label: 'Dienstverband', icon: Briefcase },
  { id: 'verzuim', label: 'Verzuim', icon: Stethoscope },
  { id: 'contracten', label: 'Contracten', icon: FileSignature },
  { id: 'documenten', label: 'Documenten', icon: FileText },
  { id: 'verlof', label: 'Verlof & Saldi', icon: CalendarDays, phase: '3' },
];

export default function MedewerkerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employee, setEmployee] = useState(null);
  const [showVerleng, setShowVerleng] = useState(false);
  const [etag, setEtag] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get('tab') || 'gegevens');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadDossierPdf = async () => {
    setPdfLoading(true);
    try {
      const resp = await api.get(`/employees/${id}/dossier-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      const msg = e.response?.status === 403 ? 'Geen rechten om dit dossier te exporteren' : 'Kon dossier-PDF niet genereren';
      setFeedback({ type: 'error', msg });
    } finally {
      setPdfLoading(false);
    }
  };

  const [showQr, setShowQr] = useState(false);
  const [qrToken, setQrToken] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrRotating, setQrRotating] = useState(false);

  // Loon/contract-wijziging t.o.v. de geladen medewerker → procesafspraak-waarschuwing.
  const payFieldsChanged = useMemo(() => {
    if (!employee) return false;
    const norm = (v) => (v === '' || v === undefined || v === null ? null : Number(v));
    return ['uurloon', 'brutosalaris', 'contract_hours'].some(
      (k) => norm(form[k]) !== norm(employee[k]));
  }, [form, employee]);

  const openQr = async () => {
    setShowQr(true);
    setQrLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/qr-token`);
      setQrToken(data.qr_token || '');
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Kon QR-code niet laden' });
      setShowQr(false);
    } finally {
      setQrLoading(false);
    }
  };

  const rotateQr = async () => {
    if (!window.confirm('Nieuwe QR-code genereren? De oude QR-code werkt daarna niet meer.')) return;
    setQrRotating(true);
    try {
      const { data } = await api.post(`/employees/${id}/qr-token`);
      setQrToken(data.qr_token || '');
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Kon QR-code niet vernieuwen' });
    } finally {
      setQrRotating(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [empResp, locResp] = await Promise.all([
          api.get(`/employees/${id}`),
          api.get('/locations'),
        ]);
        setEmployee(empResp.data);
        setEtag(empResp.headers.etag);
        setForm(empResp.data);
        setLocations(locResp.data);
      } catch (e) {
        setFeedback({ type: 'error', msg: 'Kon medewerker niet laden' });
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Fields that are part of EmployeeUpdate (whitelist; matches backend Pydantic model)
  const EDITABLE_FIELDS = [
    'name', 'email', 'telefoon', 'geslacht', 'geboortedatum',
    'adres', 'huisnummer', 'postcode', 'woonplaats', 'land',
    'role', 'home_location_id', 'assigned_locations',
    'contract_type', 'contract_hours', 'uurloon', 'brutosalaris',
    'salarisperiode', 'datum_in_dienst', 'datum_uit_dienst', 'contract_einddatum',
    'travel_distance_km', 'vacation_balance', 'overtime_balance',
    'noodcontact_naam', 'noodcontact_telefoon', 'notities',
    'bedrijfsnaam', 'pin', 'bsn',
    'loon_medewerkernummer', 'reiskosten_km_per_dienst',
  ];

  function valuesDiffer(a, b) {
    // Treat null / undefined / '' as the same when comparing
    const norm = (v) => (v === undefined || v === null || v === '' ? null : v);
    if (Array.isArray(a) || Array.isArray(b)) {
      return JSON.stringify(a ?? []) !== JSON.stringify(b ?? []);
    }
    return norm(a) !== norm(b);
  }

  async function handleSave() {
    setSaving(true);
    setFeedback({ type: '', msg: '' });
    try {
      const computeDiff = (baseline) => {
        const d = {};
        EDITABLE_FIELDS.forEach((k) => {
          if (!(k in form)) return;
          const newVal = form[k];
          const oldVal = baseline[k];
          if (valuesDiffer(newVal, oldVal)) {
            d[k] = newVal === '' ? null : newVal;
          }
        });
        if (!form._pin_input) {
          delete d.pin;
        } else {
          d.pin = form._pin_input;
        }
        return d;
      };

      const initialDiff = computeDiff(employee);
      if (Object.keys(initialDiff).length === 0) {
        setFeedback({ type: 'info', msg: 'Geen wijzigingen' });
        setSaving(false);
        return;
      }

      // We avoid any GET in the retry loop. Backend's 409 response contains
      // `current_version`, which we use to rebuild the ETag directly. This sidesteps
      // browser HTTP cache entirely (the real cause of the 409-loop bug).
      let currentVersion = Number.isInteger(employee.version) ? employee.version : 0;
      let lastError = null;
      const diff = initialDiff;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        const etagForPut = `"${id}-${currentVersion}"`;
        try {
          const { data, headers } = await api.put(
            `/employees/${id}`,
            diff,
            { headers: { 'If-Match': etagForPut } },
          );
          setEmployee(data);
          setForm({ ...data, _pin_input: '' });
          setEtag(headers.etag);
          setFeedback({ type: 'success', msg: `Opgeslagen (versie ${data.version})` });
          return;
        } catch (e) {
          lastError = e;
          const status = e.response?.status;
          const detail = e.response?.data?.detail;
          if ((status === 409 || status === 412) && detail && typeof detail === 'object' && Number.isInteger(detail.current_version)) {
            // Same version came back → genuinely stuck (or backend bug)
            if (detail.current_version === currentVersion) break;
            currentVersion = detail.current_version;
            setFeedback({ type: 'success', msg: `Versie ververst (v${currentVersion}), opnieuw opslaan… (poging ${attempt + 2}/3)` });
            continue;
          }
          // 412 or 409 without current_version → bail out
          if (status === 409 || status === 412) break;
          throw e;
        }
      }

      // Out of retries — surface clear error
      const status = lastError?.response?.status;
      let msg = 'Bijwerken mislukt';
      if (status === 409 || status === 412) {
        msg = `Opslaan mislukt na 3 pogingen. Vernieuw de pagina (F5) — DB-versie is nu v${currentVersion}.`;
      }
      setFeedback({ type: 'error', msg });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg;
      if (status === 422 && Array.isArray(detail)) {
        msg = `Validatiefout: ${detail.map((d) => d.msg || d.type).slice(0, 2).join(', ')}`;
      } else {
        msg = typeof detail === 'string' ? detail : detail?.message || 'Opslaan mislukt';
      }
      setFeedback({ type: 'error', msg });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Weet je zeker dat je ${employee.name} wilt verwijderen?`)) return;
    try {
      await api.delete(`/employees/${id}`, { headers: { 'If-Match': etag } });
      navigate('/medewerkers');
    } catch (e) {
      setFeedback({ type: 'error', msg: e.response?.data?.detail || 'Verwijderen mislukt' });
    }
  }

  if (loading) return <p className="text-sm text-slate-500">Laden…</p>;
  if (!employee) return <p className="text-sm text-slate-500">Medewerker niet gevonden</p>;

  const canDelete = hasRole('owner', 'admin');
  const isOwner = user?.role === 'owner';

  return (
    <div className="space-y-4" data-testid="page-medewerker-detail">
      <div className="flex items-center justify-between">
        <Link to="/medewerkers" className="flex items-center gap-1 text-sm text-slate-600 hover:text-brand-navy" data-testid="link-back-medewerkers">
          <ChevronLeft size={16} /> Terug naar overzicht
        </Link>
        <div className="flex items-center gap-2">
          {(user?.role === 'owner' || user?.role === 'admin') && (
            <button
              type="button"
              onClick={() => setShowVerleng(true)}
              data-testid="btn-verlengen"
              className="rounded-md border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50"
            >
              Verlengen…
            </button>
          )}
          <ContractBadge einddatum={employee.contract_einddatum} />
          <span className={`rounded px-2 py-0.5 text-xs ${employee.status === 'actief' ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'}`}>
            {employee.status}
          </span>
        </div>
      </div>

      {showVerleng && (
        <VerlengDialog
          employee={employee}
          onClose={() => setShowVerleng(false)}
          onDone={(d) => {
            setEmployee((prev) => ({ ...prev, contract_einddatum: d.contract_einddatum }));
            setForm((prev) => ({ ...prev, contract_einddatum: d.contract_einddatum }));
          }}
        />
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{employee.name}</h2>
            <p className="text-sm text-slate-600">Personeelsnummer: {employee.personeelsnummer}</p>
            {employee.bedrijfsnaam && <p className="text-xs text-slate-500 mt-1">{employee.bedrijfsnaam}</p>}
          </div>
          {hasRole('owner', 'admin', 'multi_location', 'single_location') && (
            <div className="flex items-center gap-2">
              <button
                onClick={openQr}
                data-testid="btn-qr-code"
                title="Toon/print de persoonlijke QR-code voor inklokken op de tablet"
                className="flex items-center gap-1.5 rounded-md border border-brand-navy/30 bg-white px-3 py-2 text-sm font-medium text-brand-navy hover:bg-brand-amber/10 hover:border-brand-amber transition-colors"
              >
                <QrCode size={15} />
                QR-code
              </button>
              <button
                onClick={downloadDossierPdf}
                disabled={pdfLoading}
                data-testid="btn-dossier-pdf"
                title="Genereer een printbaar HR-dossier (PDF) met dienstverband, verzuim, verlof en contracthistorie"
                className="flex items-center gap-1.5 rounded-md border border-brand-navy/30 bg-white px-3 py-2 text-sm font-medium text-brand-navy hover:bg-brand-amber/10 hover:border-brand-amber disabled:opacity-60 transition-colors"
              >
                {pdfLoading ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
                Printbaar dossier (PDF)
              </button>
            </div>
          )}
        </div>
      </div>

      {showQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="qr-modal" onClick={() => setShowQr(false)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">QR-code inklokken</h3>
                <p className="text-sm text-slate-600">{employee.name} · {employee.personeelsnummer}</p>
              </div>
              <button onClick={() => setShowQr(false)} data-testid="qr-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div id="qr-printable" className="flex flex-col items-center justify-center py-4">
              {qrLoading ? (
                <div className="py-16 text-slate-500"><Loader2 size={20} className="animate-spin" /></div>
              ) : qrToken ? (
                <>
                  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="qr-svg">
                    <QRCodeSVG value={qrToken} size={220} level="M" includeMargin />
                  </div>
                  <p className="mt-3 text-center text-sm font-semibold text-slate-900">{employee.name}</p>
                  <p className="text-center text-xs text-slate-500">{employee.personeelsnummer}</p>
                </>
              ) : (
                <p className="py-16 text-sm text-slate-500">Geen QR-code beschikbaar.</p>
              )}
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-400">
              Laat deze QR-code scannen op de inklok-tablet (tab "QR scannen").
            </p>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                onClick={rotateQr}
                disabled={qrRotating || qrLoading}
                data-testid="qr-rotate"
                className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {qrRotating ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Vernieuwen
              </button>
              <button
                onClick={() => window.print()}
                disabled={qrLoading || !qrToken}
                data-testid="qr-print"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-60"
              >
                <Printer size={14} /> Printen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              setSearchParams(t.id === 'gegevens' ? {} : { tab: t.id });
            }}
            data-testid={`tab-${t.id}`}
            className={`-mb-px flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-brand-amber text-brand-navy'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon size={14} /> {t.label}
            {t.phase && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">F{t.phase}</span>}
          </button>
        ))}
      </div>

      {feedback.msg && (
        <div className={`rounded-md px-3 py-2 text-sm ${
          feedback.type === 'error' ? 'bg-red-50 text-red-700' :
          feedback.type === 'success' ? 'bg-green-50 text-green-700' :
          'bg-slate-50 text-slate-600'
        }`} data-testid="detail-feedback">{feedback.msg}</div>
      )}

      {tab === 'gegevens' && (
        <div className="space-y-4">
          <Section title="Persoonlijk">
            <Field label="Naam" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="f-name" />
            <Field label="E-mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="f-email" />
            <Field label="Telefoon" value={form.telefoon} onChange={(v) => setForm({ ...form, telefoon: v })} testid="f-telefoon" />
            <Field label="Geslacht" value={form.geslacht} onChange={(v) => setForm({ ...form, geslacht: v })} />
            <Field label="Geboortedatum" type="date" value={form.geboortedatum} onChange={(v) => setForm({ ...form, geboortedatum: v })} />
          </Section>

          <Section title="Adres">
            <Field label="Straat" value={form.adres} onChange={(v) => setForm({ ...form, adres: v })} />
            <Field label="Huisnummer" value={form.huisnummer} onChange={(v) => setForm({ ...form, huisnummer: v })} />
            <Field label="Postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} />
            <Field label="Woonplaats" value={form.woonplaats} onChange={(v) => setForm({ ...form, woonplaats: v })} />
            <Field label="Land" value={form.land} onChange={(v) => setForm({ ...form, land: v })} />
          </Section>

          <Section title="Dienstverband">
            {payFieldsChanged && (
              <div
                className="col-span-full flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                data-testid="contract-change-warning"
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Let op: pas loon of contracturen alleen aan bij de start van een nieuwe
                  loonperiode. Een wijziging midden in een lopende periode rekent met
                  terugwerkende kracht verkeerd.
                </span>
              </div>
            )}
            <SelectField label="Rol" value={form.role} onChange={(v) => setForm({ ...form, role: v })}
              options={[['medewerker','Medewerker'],['junior','Junior'],['senior','Senior'],['teamleider','Teamleider'],['bedrijfsleider','Bedrijfsleider']]} testid="f-role" />
            <SelectField label="Thuislocatie" value={form.home_location_id || ''} onChange={(v) => setForm({ ...form, home_location_id: v })}
              options={[['', '—']].concat(locations.map((l) => [l.id, l.name]))} testid="f-location" />
            <SelectField label="Contracttype" value={form.contract_type} onChange={(v) => setForm({ ...form, contract_type: v })}
              options={[['vast','Vast'],['tijdelijk','Tijdelijk'],['oproep','Oproep']]} testid="f-contract" />
            <Field label="Uren/week" type="number" value={form.contract_hours} onChange={(v) => setForm({ ...form, contract_hours: parseFloat(v) || 0 })} testid="f-hours" />
            {form.contract_type === 'oproep' ? (              <>
                <Field label="Uurloon (leidend bij oproep)" type="number" step="0.01" value={form.uurloon || ''}
                  onChange={(v) => setForm({ ...form, uurloon: parseFloat(v) || null })} testid="f-uurloon" />
                <Field label="Brutosalaris (secundair)" type="number" step="0.01" value={form.brutosalaris || ''}
                  onChange={(v) => setForm({ ...form, brutosalaris: parseFloat(v) || null })} testid="f-brutosalaris" />
                <SelectField label="Salarisperiode (secundair)" value={form.salarisperiode || ''}
                  onChange={(v) => setForm({ ...form, salarisperiode: v || null })}
                  options={[['', '—'], ['per_maand', 'Per maand'], ['per_4_weken', 'Per 4 weken']]} testid="f-salarisperiode" />
              </>
            ) : (
              <>
                <Field label="Brutosalaris (leidend bij vast/tijdelijk)" type="number" step="0.01" value={form.brutosalaris || ''}
                  onChange={(v) => setForm({ ...form, brutosalaris: parseFloat(v) || null })} testid="f-brutosalaris" />
                <SelectField label="Salarisperiode" value={form.salarisperiode || ''}
                  onChange={(v) => setForm({ ...form, salarisperiode: v || null })}
                  options={[['', '—'], ['per_maand', 'Per maand'], ['per_4_weken', 'Per 4 weken']]} testid="f-salarisperiode" />
                <Field label="Uurloon (secundair)" type="number" step="0.01" value={form.uurloon || ''}
                  onChange={(v) => setForm({ ...form, uurloon: parseFloat(v) || null })} testid="f-uurloon" />
              </>
            )}
            <Field label="Datum in dienst" type="date" value={form.datum_in_dienst} onChange={(v) => setForm({ ...form, datum_in_dienst: v })} testid="f-startdate" />
            <Field label="Contract t/m" type="date" value={form.contract_einddatum || ''}
              onChange={(v) => setForm({ ...form, contract_einddatum: v || null })} testid="f-contract-einddatum"
              help="Einddatum van het huidige contract — informatief, geen invloed op planning of verloning" />
            <VerlengHistorie employeeId={employee.id} refreshKey={employee.contract_einddatum} />
            <Field label="Datum uit dienst" type="date" value={form.datum_uit_dienst} onChange={(v) => setForm({ ...form, datum_uit_dienst: v })} testid="f-enddate" />
            <Field label="Bedrijfsnaam" value={form.bedrijfsnaam} onChange={(v) => setForm({ ...form, bedrijfsnaam: v })} />
            <Field label="Reisafstand (km)" type="number" step="0.1" value={form.travel_distance_km || ''} onChange={(v) => setForm({ ...form, travel_distance_km: parseFloat(v) || null })} />
          </Section>

          <Section title="Salarisexport (Exact)">
            <Field
              label="Loon-medewerkernummer (Exact)"
              value={form.loon_medewerkernummer}
              onChange={(v) => setForm({ ...form, loon_medewerkernummer: v })}
              testid="f-loon-medewerkernummer"
              help="Leeg = personeelsnummer wordt gebruikt voor de export"
            />
            <Field
              label="Reiskosten km per dienst"
              type="number"
              step="0.1"
              value={form.reiskosten_km_per_dienst ?? ''}
              onChange={(v) => setForm({ ...form, reiskosten_km_per_dienst: v === '' ? null : (parseFloat(v) || 0) })}
              testid="f-reiskosten-km"
              help="Standaard km die per gewerkte dienst wordt geteld; per dienst aanpasbaar"
            />
            <div className="col-span-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="emp-effective-export-number">
              Effectief exportnummer:{' '}
              <strong className="text-slate-900">
                {(form.loon_medewerkernummer || '').toString().trim() || employee.personeelsnummer || '—'}
              </strong>
              {!(form.loon_medewerkernummer || '').toString().trim() && (
                <span className="text-slate-400"> (personeelsnummer)</span>
              )}
            </div>
          </Section>

          <Section title="Noodcontact">
            <Field label="Naam" value={form.noodcontact_naam} onChange={(v) => setForm({ ...form, noodcontact_naam: v })} />
            <Field label="Telefoon" value={form.noodcontact_telefoon} onChange={(v) => setForm({ ...form, noodcontact_telefoon: v })} />
          </Section>

          <KostprijsCard employeeId={id} reloadKey={employee.version} />

          <Section title="Portal-toegang & Notities">
            <Field label="PIN (4-cijferig, laat leeg om niet te wijzigen)" type="password" maxLength={4}
              value={form._pin_input || ''} onChange={(v) => setForm({ ...form, _pin_input: v, pin: v })} testid="f-pin" />
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notities (intern)</label>
              <textarea rows={3} value={form.notities || ''} onChange={(e) => setForm({ ...form, notities: e.target.value })}
                data-testid="f-notities"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
            </div>
          </Section>

          {isOwner && (
            <Section title="Vertrouwelijk (alleen Owner)">
              <Field label="BSN" value={form.bsn || ''} onChange={(v) => setForm({ ...form, bsn: v })} testid="f-bsn" />
            </Section>
          )}

          <div className="flex justify-between items-center pt-2">
            <div className="text-xs text-slate-500">
              Aangemaakt: {formatDateNL(employee.created_at)} · Versie: {employee.version}
            </div>
            <div className="flex gap-2">
              {canDelete && (
                <button onClick={handleDelete} data-testid="btn-delete-employee"
                  className="flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50">
                  <Trash2 size={14} /> Verwijderen
                </button>
              )}
              <button onClick={handleSave} disabled={saving} data-testid="btn-save-employee"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
                <Save size={14} /> {saving ? 'Bezig…' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'dienstverband' && (
        <DienstverbandTab
          employeeId={id}
          employee={employee}
          canEdit={hasRole('owner', 'admin', 'multi_location', 'single_location')}
          onEmployeeUpdate={(emp) => { setEmployee(emp); setForm({ ...emp, _pin_input: '' }); }}
        />
      )}

      {tab === 'verzuim' && (
        <VerzuimTab
          employeeId={id}
          canEdit={hasRole('owner', 'admin', 'multi_location', 'single_location')}
        />
      )}

      {tab === 'documenten' && (
        <DocumentenSection
          employeeId={id}
          canEdit={hasRole('owner', 'admin', 'multi_location', 'single_location')}
        />
      )}

      {tab === 'contracten' && (
        <ContractenTab
          employeeId={id}
          employee={employee}
          canGenerate={hasRole('owner', 'admin', 'multi_location', 'single_location')}
        />
      )}

      {tab === 'verlof' && (
        <VerlofTab
          employeeId={id}
          locations={locations}
          employee={employee}
          canEdit={hasRole('owner', 'admin', 'multi_location', 'single_location')}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide text-xs text-slate-500">{title}</h3>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function KostRow({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className={strong ? 'font-semibold text-slate-800' : 'text-slate-600'}>{label}</span>
      <span className={`tabular-nums ${strong ? 'font-semibold text-brand-navy' : 'text-slate-700'}`}>{value}</span>
    </div>
  );
}

function KostprijsCard({ employeeId, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api.get(`/loonkosten/employee/${employeeId}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId, reloadKey]);

  const eur = (n) => (n == null ? '—' : `€${Number(n).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const FLAG_LABELS = {
    geen_uurloon: 'Geen uurloon ingevuld — geen kostprijs',
    geen_brutosalaris: 'Geen loonbasis (uurloon/brutosalaris) — geen kostprijs',
    leeftijd_onbekend: 'Geboortedatum onbekend — pensioen op €0',
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="emp-kostprijs-card">
      <h3 className="text-sm font-semibold mb-3 uppercase tracking-wide text-xs text-slate-500">
        Loonkosten per uur (indicatief)
      </h3>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={14} /> Berekenen…</div>
      ) : error ? (
        <div className="text-sm text-slate-500">Kon loonkosten niet berekenen.</div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-2xl font-bold text-brand-navy tabular-nums" data-testid="emp-kostprijs-amount">
              {data.kostprijs_per_uur == null ? '—' : eur(data.kostprijs_per_uur)}
            </span>
            <span className="text-xs text-slate-500">per gewerkt uur · loonjaar {data.loon_year}</span>
          </div>

          {(data.flags || []).length > 0 && (
            <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="emp-kostprijs-flags">
              <ul className="list-disc pl-4 space-y-0.5">
                {data.flags.map((f) => <li key={f}>{FLAG_LABELS[f] || f}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-md border border-slate-100 divide-y divide-slate-100 text-sm">
            <KostRow label="Loonbasis" value={eur(data.opbouw?.loonbasis)} />
            <KostRow label="+ vakantie(geld/toeslag)" value={eur(data.opbouw?.loon_incl_vak)} />
            <KostRow label={`+ werkgeverspremies (${data.opbouw?.premies_pct ?? '—'}%)`} value={`+ ${eur(data.opbouw?.premies_bedrag)}`} />
            <KostRow label="+ pensioen (wg)" value={`+ ${eur(data.opbouw?.pensioen)}`} />
            {data.contract_type !== 'oproep' && (
              <>
                <KostRow label="= kost per contractuur" value={eur(data.opbouw?.kost_per_contractuur)} strong />
                <KostRow label={`× productieve correctie (${data.opbouw?.productive_factor ?? '—'})`} value="" />
              </>
            )}
            <KostRow label="= kostprijs per uur" value={eur(data.kostprijs_per_uur)} strong />
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Aparte kosten-/rapportagelaag. Raakt de salarisberekening niet aan.
            {data.leeftijd != null ? ` Leeftijd ${data.leeftijd}.` : ''}
          </p>
        </>
      )}
    </div>
  );
}


function VerlengHistorie({ employeeId, refreshKey }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/employees/${employeeId}/verleng-historie`)
      .then((r) => { if (alive) setItems(r.data.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [employeeId, refreshKey]);

  if (!items || items.length === 0) return null;
  return (
    <div className="sm:col-span-2" data-testid="verleng-historie">
      <p className="text-xs font-medium text-slate-600">Verleng-historie</p>
      <ul className="mt-1 space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-[11px] text-slate-500" data-testid={`verleng-historie-item-${i}`}>
            {it.timestamp ? new Date(it.timestamp).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
            {' · '}{it.user_email}
            {' · '}{it.van ? formatDateNL(it.van) : 'leeg'} → {it.naar ? formatDateNL(it.naar) : 'leeg'}
            {it.reason && <span className="text-slate-400"> · "{it.reason}"</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContractBadge({ einddatum }) {
  const state = contractStatus(einddatum);
  if (state !== 'verlopen' && state !== 'bijna') return null;
  const nl = einddatum.split('-').reverse().join('-');
  return (
    <span
      data-testid={`contract-badge-${state}`}
      className={`rounded px-2 py-0.5 text-xs font-medium ${
        state === 'verlopen' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}
      title={`Contract t/m ${nl}`}
    >
      {state === 'verlopen' ? `Contract verlopen (t/m ${nl})` : `Loopt af ≤30 dagen (t/m ${nl})`}
    </span>
  );
}

function Field({ label, value, onChange, type = 'text', maxLength, step, min, testid, autoComplete, help }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength} step={step} min={min}
        autoComplete={autoComplete ?? (type === 'password' ? 'new-password' : 'off')}
        data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-amber focus:outline-none focus:ring-1 focus:ring-brand-amber/40"
      />
      {help && <p className="mt-1 text-[11px] text-slate-500">{help}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}



const LEAVE_TYPES = [
  ['vakantie', 'Vakantie', 'bg-emerald-100 text-emerald-700'],
  ['ziekte', 'Ziek', 'bg-rose-100 text-rose-700'],
  ['bijzonder_verlof', 'Bijzonder verlof', 'bg-sky-100 text-sky-700'],
  ['re_integratie', 'Re-integratie', 'bg-violet-100 text-violet-700'],
  ['onbetaald', 'Onbetaald verlof', 'bg-amber-100 text-amber-700'],
];
const LEAVE_TYPE_LABEL = Object.fromEntries(LEAVE_TYPES.map(([v, l]) => [v, l]));
const LEAVE_TYPE_BADGE = Object.fromEntries(LEAVE_TYPES.map(([v, , c]) => [v, c]));

const LEAVE_STATUS_META = {
  in_afwachting: { label: 'In afwachting', badge: 'bg-amber-100 text-amber-700' },
  goedgekeurd: { label: 'Goedgekeurd', badge: 'bg-emerald-100 text-emerald-700' },
  afgewezen: { label: 'Afgewezen', badge: 'bg-rose-100 text-rose-700' },
};

const CONTRACT_CHANGE_TYPES = [
  ['uren', 'Uren'],
  ['salaris', 'Salaris'],
  ['functie', 'Functie'],
  ['looptijd', 'Looptijd'],
  ['overig', 'Overig'],
];
const CONTRACT_CHANGE_LABEL = Object.fromEntries(CONTRACT_CHANGE_TYPES.map(([v, l]) => [v, l]));
const CONTRACT_CHANGE_BADGE = {
  uren: 'bg-sky-100 text-sky-700', salaris: 'bg-emerald-100 text-emerald-700',
  functie: 'bg-violet-100 text-violet-700', looptijd: 'bg-amber-100 text-amber-700',
  overig: 'bg-slate-100 text-slate-700',
};

const SUGGEST_SRC = { rooster: 'rooster', 'contract/5': 'contract ÷ 5', none: 'geen' };

function VerlofTab({ employeeId, locations, employee, canEdit }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  // The employee's own locations (home + assigned UUIDs) resolved to {slug,name}.
  const empLocations = useMemo(() => {
    const ids = [employee?.home_location_id, ...(employee?.assigned_locations || [])].filter(Boolean);
    const byId = new Map();
    (locations || []).forEach((l) => { if (ids.includes(l.id)) byId.set(l.id, l); });
    return Array.from(byId.values());
  }, [employee, locations]);

  const homeSlug = useMemo(
    () => (locations || []).find((l) => l.id === employee?.home_location_id)?.slug || '',
    [locations, employee],
  );
  const defaultSlug = homeSlug || empLocations[0]?.slug || (locations || [])[0]?.slug || '';
  const formLocations = empLocations.length ? empLocations : (locations || []);

  const blankForm = () => ({
    type: 'vakantie', start_date: '', end_date: '', hours: 8, location_id: defaultSlug, notes: '', approve_now: true,
  });
  const [form, setForm] = useState(blankForm());
  const [suggestion, setSuggestion] = useState(null);

  // Auto-suggest leave hours (planned shift hours, else contract/5 for vast).
  // Pre-fills the hours field on CREATE; on EDIT it only shows as a hint.
  useEffect(() => {
    if (!showForm || !employeeId || !form.start_date || !form.end_date
        || form.end_date < form.start_date) {
      setSuggestion(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/leave/suggest-hours', {
          params: { employee_id: employeeId, start_date: form.start_date, end_date: form.end_date },
        });
        if (cancelled) return;
        setSuggestion(data);
        if (!editing) setForm((f) => ({ ...f, hours: data.suggested_hours }));
      } catch {
        if (!cancelled) setSuggestion(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, employeeId, form.start_date, form.end_date, editing]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/leave', { params: { employee_id: employeeId } });
      setRecords(data.records || []);
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Kon verlof niet laden' });
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(blankForm()); setFeedback({ type: '', msg: '' }); setShowForm(true); };
  const openEdit = (rec) => {
    setEditing(rec);
    setForm({ type: rec.type, start_date: rec.start_date, end_date: rec.end_date, hours: rec.hours, location_id: rec.location_id, notes: rec.notes || '' });
    setFeedback({ type: '', msg: '' });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  // Optimistic-lock conflict (mirrors the rest of the page).
  const handleConflict = async (e) => {
    const status = e.response?.status;
    if (status === 409 || status === 412) {
      setFeedback({ type: 'error', msg: 'Vernieuw de pagina, verlof is door iemand anders gewijzigd' });
      await load();
      return true;
    }
    return false;
  };

  const save = async () => {
    if (!form.start_date || !form.end_date) { setFeedback({ type: 'error', msg: 'Vul start- en einddatum in' }); return; }
    if (form.end_date < form.start_date) { setFeedback({ type: 'error', msg: 'Einddatum moet ≥ startdatum zijn' }); return; }
    setSaving(true);
    try {
      const hours = parseFloat(form.hours) || 0;
      if (editing) {
        // LeaveUpdate: type/dates/hours/notes (no location_id).
        const payload = { type: form.type, start_date: form.start_date, end_date: form.end_date, hours, notes: form.notes || null };
        await api.put(`/leave/${editing.id}`, payload, { headers: { 'If-Match': `"${editing.id}-${editing.version}"` } });
      } else {
        await api.post('/leave', {
          employee_id: employeeId, location_id: form.location_id, type: form.type,
          start_date: form.start_date, end_date: form.end_date, hours, notes: form.notes || null,
          status: form.approve_now ? 'goedgekeurd' : 'in_afwachting',
        });
      }
      closeForm();
      setFeedback({ type: 'success', msg: editing ? 'Verlof bijgewerkt' : (form.approve_now ? 'Verlof toegevoegd & goedgekeurd' : 'Verlofaanvraag ingediend (in afwachting)') });
      await load();
    } catch (e) {
      if (await handleConflict(e)) return;
      const status = e.response?.status;
      if (status === 422) {
        const d = e.response?.data?.detail;
        setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Validatiefout in het formulier' });
      } else {
        setFeedback({ type: 'error', msg: 'Opslaan mislukt' });
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Verlof verwijderen (${LEAVE_TYPE_LABEL[rec.type] || rec.type}, ${formatDateNL(rec.start_date)})?`)) return;
    try {
      await api.delete(`/leave/${rec.id}`, { headers: { 'If-Match': `"${rec.id}-${rec.version}"` } });
      setFeedback({ type: 'success', msg: 'Verlof verwijderd' });
      await load();
    } catch (e) {
      if (await handleConflict(e)) return;
      setFeedback({ type: 'error', msg: 'Verwijderen mislukt' });
    }
  };

  const decide = async (rec, action) => {
    try {
      await api.post(`/leave/${rec.id}/${action}`, {}, { headers: { 'If-Match': `"${rec.id}-${rec.version}"` } });
      setFeedback({ type: 'success', msg: action === 'approve' ? 'Verlof goedgekeurd' : 'Verlof afgewezen' });
      await load();
    } catch (e) {
      if (await handleConflict(e)) return;
      setFeedback({ type: 'error', msg: 'Actie mislukt' });
    }
  };

  return (
    <div className="space-y-4" data-testid="verlof-tab">
      {feedback.msg && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
          data-testid="verlof-feedback"
        >
          {feedback.msg}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <CalendarDays size={18} /> Verlof & Saldi
            </h3>
            <p className="mt-1 text-sm text-slate-600" data-testid="verlof-saldo">
              Vakantiesaldo: <strong className="tabular-nums">{employee?.vacation_balance ?? 0}</strong> u
            </p>
          </div>
          {canEdit && !showForm && (
            <button
              onClick={openNew}
              data-testid="btn-new-verlof"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
            >
              <Plus size={14} /> Nieuw verlof
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Alleen <strong>Vakantie</strong>, <strong>Ziek</strong> en <strong>Onbetaald verlof</strong> tellen momenteel mee in de salarisexport.
        </p>
      </div>

      {showForm && canEdit && (
        <div className="rounded-xl border-2 border-brand-amber/30 bg-brand-amber/5 p-5" data-testid="verlof-form">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">{editing ? 'Verlof bewerken' : 'Nieuw verlof'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Type"
              value={form.type}
              onChange={(v) => setForm({ ...form, type: v })}
              options={LEAVE_TYPES.map(([v, l]) => [v, l])}
              testid="verlof-type"
            />
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Locatie</label>
              <select
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                disabled={!!editing}
                data-testid="verlof-location"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-500"
              >
                {formLocations.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
              </select>
              {editing && <p className="mt-1 text-[11px] text-slate-500">Locatie is niet wijzigbaar bij bewerken.</p>}
            </div>
            <Field label="Startdatum" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="verlof-start" />
            <Field label="Einddatum" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="verlof-end" />
            <Field label="Uren" type="number" step="0.5" min="0" value={form.hours} onChange={(v) => setForm({ ...form, hours: v })} testid="verlof-hours" />
            {suggestion && (
              <div
                className="col-span-2 -mt-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px] text-slate-600"
                data-testid="verlof-hours-suggestion"
              >
                <div className="flex items-center justify-between gap-2">
                  <span>
                    Suggestie: <strong>{suggestion.suggested_hours} u</strong> — gepland rooster, anders
                    contract ÷ 5. Aanpasbaar.
                  </span>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, hours: suggestion.suggested_hours }))}
                    className="shrink-0 font-medium text-brand-navy underline"
                    data-testid="verlof-hours-apply-suggestion"
                  >
                    Overnemen
                  </button>
                </div>
                {suggestion.per_day?.length > 0 && (
                  <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {suggestion.per_day.map((d) => (
                      <li key={d.date}>
                        {formatDateNL(d.date)}: {d.hours}u{' '}
                        <span className="text-slate-400">({SUGGEST_SRC[d.source] || d.source})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notitie (optioneel)</label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="verlof-notes"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
            {!editing && (
              <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700" data-testid="verlof-approve-now-wrap">
                <input
                  type="checkbox"
                  checked={form.approve_now}
                  onChange={(e) => setForm({ ...form, approve_now: e.target.checked })}
                  data-testid="verlof-approve-now"
                  className="h-4 w-4 rounded border-slate-300"
                />
                Direct goedkeuren (anders ingediend als <strong>aanvraag</strong> ter goedkeuring)
              </label>
            )}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={closeForm} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="btn-save-verlof"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? 'Bijwerken' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : records.length === 0 ? (
          <div className="p-10 text-center" data-testid="verlof-empty">
            <CalendarDays size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Nog geen verlof geregistreerd</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Periode</th>
                  <th className="py-2 px-3 text-right">Uren</th>
                  <th className="py-2 px-3">Notitie</th>
                  {canEdit && <th className="py-2 px-3 text-right">Acties</th>}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id} data-testid={`verlof-row-${rec.id}`} className="border-b border-slate-100">
                    <td className="py-2 px-3">
                      <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${LEAVE_TYPE_BADGE[rec.type] || 'bg-slate-100 text-slate-700'}`}>
                        {LEAVE_TYPE_LABEL[rec.type] || rec.type}
                      </span>
                    </td>
                    <td className="py-2 px-3" data-testid={`verlof-status-${rec.id}`}>
                      <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${(LEAVE_STATUS_META[rec.status] || {}).badge || 'bg-slate-100 text-slate-700'}`}>
                        {(LEAVE_STATUS_META[rec.status] || {}).label || rec.status || '—'}
                      </span>
                    </td>
                    <td className="py-2 px-3 tabular-nums text-slate-700">{formatDateNL(rec.start_date)} – {formatDateNL(rec.end_date)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{rec.hours}</td>
                    <td className="py-2 px-3 text-slate-600">{rec.notes || '—'}</td>
                    {canEdit && (
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {rec.status === 'in_afwachting' && (
                          <>
                            <button onClick={() => decide(rec, 'approve')} data-testid={`btn-approve-verlof-${rec.id}`}
                              className="mr-1 inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50"><CheckCircle2 size={13} /> Goedkeuren</button>
                            <button onClick={() => decide(rec, 'reject')} data-testid={`btn-reject-verlof-${rec.id}`}
                              className="mr-1 inline-flex items-center gap-1 rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50">Afwijzen</button>
                          </>
                        )}
                        <button onClick={() => openEdit(rec)} data-testid={`btn-edit-verlof-${rec.id}`}
                          className="mr-1 inline-flex rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy"><Pencil size={14} /></button>
                        <button onClick={() => remove(rec)} data-testid={`btn-delete-verlof-${rec.id}`}
                          className="inline-flex rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ContractenTab({ employeeId, employee, canGenerate }) {
  const [generations, setGenerations] = useState([]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ effective_date: today, change_type: 'uren', summary: '', reason: '', linked_contract_document_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [genResp, chgResp] = await Promise.all([
        api.get('/contracts/generations', { params: { employee_id: employeeId } }),
        api.get(`/employees/${employeeId}/contract-changes`),
      ]);
      setGenerations(genResp.data.items || []);
      setChanges(chgResp.data.changes || []);
    } catch (e) {
      console.error('[medewerker-detail] load contracten failed', e);
      setFeedback({ type: 'error', msg: 'Kon contracthistorie niet laden' });
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const onDownload = async (documentId, title) => {
    if (!documentId) return;
    try {
      const resp = await api.get(`/documents/${documentId}/download`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'contract'}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      alert('Download mislukt');
    }
  };

  const currentHint = useMemo(() => {
    if (!employee) return '';
    switch (form.change_type) {
      case 'uren': return `Huidig: ${employee.contract_hours ?? '—'} u/week`;
      case 'salaris': return `Huidig: ${employee.uurloon ? `€${employee.uurloon}/u` : (employee.brutosalaris ? `€${employee.brutosalaris} bruto` : '—')}`;
      case 'functie': return `Huidige rol: ${employee.role || '—'} · contract: ${employee.contract_type || '—'}`;
      case 'looptijd': return `In dienst: ${employee.datum_in_dienst ? formatDateNL(employee.datum_in_dienst) : '—'}${employee.datum_uit_dienst ? ` · uit: ${formatDateNL(employee.datum_uit_dienst)}` : ''}`;
      default: return '';
    }
  }, [employee, form.change_type]);

  const submitChange = async () => {
    if (!form.effective_date) { setFeedback({ type: 'error', msg: 'Vul een ingangsdatum in' }); return; }
    if (!form.summary.trim()) { setFeedback({ type: 'error', msg: 'Beschrijf kort wat er wijzigt' }); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/contract-changes`, {
        effective_date: form.effective_date,
        change_type: form.change_type,
        summary: form.summary.trim(),
        reason: form.reason || null,
        linked_contract_document_id: form.linked_contract_document_id || null,
      });
      setShowForm(false);
      setForm({ effective_date: today, change_type: 'uren', summary: '', reason: '', linked_contract_document_id: '' });
      setFeedback({ type: 'success', msg: 'Contractwijziging vastgelegd' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  const removeChange = async (chg) => {
    if (!window.confirm(`Contractwijziging (${formatDateNL(chg.effective_date)}) verwijderen?`)) return;
    try {
      await api.delete(`/employees/${employeeId}/contract-changes/${chg.id}`, { headers: { 'If-Match': `"${chg.id}-${chg.version ?? 0}"` } });
      setFeedback({ type: 'success', msg: 'Wijziging verwijderd' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Verwijderen mislukt' });
    }
  };

  // Merged timeline (changes + generations), newest first.
  const timeline = useMemo(() => {
    const items = [
      ...changes.map((c) => ({ kind: 'change', date: c.effective_date, raw: c })),
      ...generations.map((g) => ({ kind: 'generation', date: (g.generated_at || '').slice(0, 10), raw: g })),
    ];
    items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return items;
  }, [changes, generations]);

  const lastChangeId = changes.length ? changes[0].id : null; // changes already newest-first from API

  return (
    <div className="space-y-4" data-testid="contracten-tab">
      {feedback.msg && (
        <div className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="contracten-feedback">
          {feedback.msg}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <FileSignature size={18} /> Contracthistorie
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Gegenereerde contracten + vastgelegde wijzigingen (nieuwste eerst).
            </p>
          </div>
          <div className="flex gap-2">
            {canGenerate && !showForm && (
              <button onClick={() => { setShowForm(true); setFeedback({ type: '', msg: '' }); }} data-testid="btn-record-change"
                className="flex items-center gap-1.5 rounded-md border border-brand-navy/30 px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-slate-50">
                <Pencil size={14} /> Wijziging vastleggen
              </button>
            )}
            {canGenerate && (
              <Link to={`/contracten?employee_id=${employeeId}`} data-testid="btn-new-contract"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light">
                <Plus size={14} /> Nieuw contract
              </Link>
            )}
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Let op: dit is een dossier-/historielaag. De salarisberekening blijft de <strong>huidige</strong> medewerkergegevens gebruiken; effectief-gedateerde verloning per periode is een aparte, toekomstige stap.
        </p>
      </div>

      {showForm && canGenerate && (
        <div className="rounded-xl border-2 border-brand-amber/30 bg-brand-amber/5 p-5" data-testid="contract-change-form">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Contractwijziging vastleggen</h4>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ingangsdatum" type="date" value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} testid="cc-effective-date" />
            <SelectField label="Type wijziging" value={form.change_type} onChange={(v) => setForm({ ...form, change_type: v })} options={CONTRACT_CHANGE_TYPES} testid="cc-type" />
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Samenvatting (oud → nieuw)</label>
              <input type="text" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="cc-summary"
                placeholder="bijv. Uren 32 → 40 per week"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
              {currentHint && <p className="mt-1 text-[11px] text-slate-500">{currentHint}</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Reden (optioneel)</label>
              <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cc-reason"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
            </div>
            {generations.length > 0 && (
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Koppel aan gegenereerd contract (optioneel)</label>
                <select value={form.linked_contract_document_id} onChange={(e) => setForm({ ...form, linked_contract_document_id: e.target.value })} data-testid="cc-link"
                  className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm">
                  <option value="">— Geen —</option>
                  {generations.filter((g) => g.document_id).map((g) => (
                    <option key={g.id} value={g.document_id}>{g.template_title} ({formatDateNL(g.generated_at)})</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
            <button onClick={submitChange} disabled={saving} data-testid="btn-save-contract-change"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Vastleggen
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : timeline.length === 0 ? (
          <div className="py-8 text-center" data-testid="contracten-empty">
            <FileSignature size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Nog geen contracten of wijzigingen vastgelegd.</p>
            {canGenerate && (
              <Link to={`/contracten?employee_id=${employeeId}`} className="mt-3 inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50">
                <Plus size={12} /> Eerste contract genereren
              </Link>
            )}
          </div>
        ) : (
          <ol className="relative ml-2 border-l-2 border-slate-200" data-testid="contracten-timeline">
            {timeline.map((item, idx) => {
              const isCurrent = idx === 0;
              if (item.kind === 'change') {
                const c = item.raw;
                return (
                  <li key={`c-${c.id}`} className="mb-6 ml-6 last:mb-0" data-testid={`contract-change-${c.id}`}>
                    <span className="absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white bg-amber-500" />
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${CONTRACT_CHANGE_BADGE[c.change_type] || 'bg-slate-100 text-slate-700'}`}>
                            Wijziging · {CONTRACT_CHANGE_LABEL[c.change_type] || c.change_type}
                          </span>
                          <span className="text-sm font-semibold text-slate-900 tabular-nums">{formatDateNL(c.effective_date)}</span>
                          {isCurrent && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Huidig</span>}
                        </div>
                        {c.summary && <p className="mt-1 text-sm text-slate-700">{c.summary}</p>}
                        {c.reason && <p className="mt-0.5 text-xs text-slate-500 italic">Reden: {c.reason}</p>}
                        <p className="mt-1 text-[11px] text-slate-400">Vastgelegd door {c.created_by_name || '—'}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {c.linked_contract_document_id && (
                          <button onClick={() => onDownload(c.linked_contract_document_id, 'contract')} data-testid={`cc-download-${c.id}`}
                            title="Gekoppeld contract" className="inline-flex rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy"><Download size={14} /></button>
                        )}
                        {canGenerate && c.id === lastChangeId && (
                          <button onClick={() => removeChange(c)} data-testid={`btn-delete-change-${c.id}`}
                            className="inline-flex rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              }
              const g = item.raw;
              return (
                <li key={`g-${g.id}`} className="mb-6 ml-6 last:mb-0" data-testid={`generation-${g.id}`}>
                  <span className="absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white bg-brand-navy" />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                          <FileSignature size={12} /> Contract
                        </span>
                        <span className="text-sm font-semibold text-slate-900">{g.template_title}</span>
                        <span className="text-xs text-slate-500 tabular-nums">{formatDateNL(g.generated_at)}</span>
                        {isCurrent && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Huidig</span>}
                      </div>
                      <p className="mt-1 text-[11px] text-slate-400">Gegenereerd door {g.generated_by_name || '—'}</p>
                    </div>
                    {g.document_id && (
                      <button onClick={() => onDownload(g.document_id, g.template_title)} data-testid={`gen-download-${g.id}`}
                        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy" title="Download PDF"><Download size={14} /></button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <p className="text-[10px] text-slate-400">
        Gegenereerde contracten zijn ook beschikbaar in de tab <strong>Documenten</strong> als categorie "Contract".
      </p>
    </div>
  );
}

const EVENT_META = {
  in_dienst: { label: 'In dienst', icon: LogIn, badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  re_indienst: { label: 'Opnieuw in dienst', icon: RotateCcw, badge: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' },
  uit_dienst: { label: 'Uit dienst', icon: LogOut, badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
};
const UIT_REASONS = [
  ['', '— Geen reden —'],
  ['vrijwillig', 'Vrijwillig vertrek'],
  ['ontslag', 'Ontslag'],
  ['einde_contract', 'Einde contract'],
  ['pensioen', 'Pensioen'],
  ['anders', 'Anders (zie notitie)'],
];
const UIT_REASON_LABEL = Object.fromEntries(UIT_REASONS.map(([v, l]) => [v, l]));

function DienstverbandTab({ employeeId, employee, canEdit, onEmployeeUpdate }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null); // 'uit_dienst' | 're_indienst'
  const [form, setForm] = useState({ effective_date: '', reason: '', notes: '' });
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/employment-events`);
      setEvents(data.events || []);
    } catch {
      setFeedback({ type: 'error', msg: 'Kon dienstverband-historie niet laden' });
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const isActive = employee?.status === 'actief';
  const today = new Date().toISOString().slice(0, 10);

  const openForm = (m) => {
    setMode(m);
    setForm({ effective_date: today, reason: '', notes: '' });
    setFeedback({ type: '', msg: '' });
  };
  const closeForm = () => { setMode(null); };

  const submit = async () => {
    if (!form.effective_date) { setFeedback({ type: 'error', msg: 'Vul een datum in' }); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/employment-events`, {
        type: mode,
        effective_date: form.effective_date,
        reason: mode === 'uit_dienst' ? (form.reason || null) : null,
        notes: form.notes || null,
      });
      if (data.employee && onEmployeeUpdate) onEmployeeUpdate(data.employee);
      setFeedback({ type: 'success', msg: mode === 'uit_dienst' ? 'Uit dienst gemeld' : 'Opnieuw in dienst gemeld' });
      closeForm();
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async (ev) => {
    if (ev.derived) { setFeedback({ type: 'error', msg: 'Automatisch afgeleide gebeurtenis kan niet worden verwijderd' }); return; }
    if (!window.confirm(`Gebeurtenis "${EVENT_META[ev.type]?.label || ev.type}" (${formatDateNL(ev.effective_date)}) verwijderen?`)) return;
    try {
      const { data } = await api.delete(`/employees/${employeeId}/employment-events/${ev.id}`, {
        headers: { 'If-Match': `"${ev.id}-${ev.version ?? 0}"` },
      });
      if (data.employee && onEmployeeUpdate) onEmployeeUpdate(data.employee);
      setFeedback({ type: 'success', msg: 'Gebeurtenis verwijderd' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Verwijderen mislukt' });
    }
  };

  const lastId = events.length ? events[events.length - 1].id : null;

  return (
    <div className="space-y-4" data-testid="dienstverband-tab">
      {feedback.msg && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : feedback.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-600'}`}
          data-testid="dienstverband-feedback"
        >
          {feedback.msg}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Briefcase size={18} /> Dienstverband
            </h3>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-600">
              <span>Huidige status:</span>
              <span
                data-testid="dienstverband-status"
                className={`rounded px-2 py-0.5 text-xs font-medium ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}
              >
                {employee?.status || '—'}
              </span>
              {employee?.datum_in_dienst && (
                <span className="text-xs text-slate-400">· In dienst sinds {formatDateNL(employee.datum_in_dienst)}</span>
              )}
              {employee?.datum_uit_dienst && (
                <span className="text-xs text-slate-400">· Uit dienst per {formatDateNL(employee.datum_uit_dienst)}</span>
              )}
            </div>
          </div>
          {canEdit && !mode && (
            isActive ? (
              <button
                onClick={() => openForm('uit_dienst')}
                data-testid="btn-uit-dienst"
                className="flex items-center gap-1.5 rounded-md border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
              >
                <LogOut size={14} /> Uit dienst melden
              </button>
            ) : (
              <button
                onClick={() => openForm('re_indienst')}
                data-testid="btn-re-indienst"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
              >
                <RotateCcw size={14} /> Opnieuw in dienst
              </button>
            )
          )}
        </div>
      </div>

      {mode && canEdit && (
        <div className="rounded-xl border-2 border-brand-amber/30 bg-brand-amber/5 p-5" data-testid="dienstverband-form">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">
            {mode === 'uit_dienst' ? 'Uit dienst melden' : 'Opnieuw in dienst melden'}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={mode === 'uit_dienst' ? 'Laatste werkdag / uit dienst per' : 'Opnieuw in dienst per'}
              type="date"
              value={form.effective_date}
              onChange={(v) => setForm({ ...form, effective_date: v })}
              testid="dienstverband-date"
            />
            {mode === 'uit_dienst' && (
              <SelectField
                label="Reden"
                value={form.reason}
                onChange={(v) => setForm({ ...form, reason: v })}
                options={UIT_REASONS}
                testid="dienstverband-reason"
              />
            )}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notitie (optioneel)</label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="dienstverband-notes"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={closeForm} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="btn-save-dienstverband"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Bevestigen
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-slate-900 mb-4">Tijdlijn</h4>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : events.length === 0 ? (
          <div className="py-8 text-center" data-testid="dienstverband-empty">
            <Briefcase size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Nog geen dienstverband-historie</p>
          </div>
        ) : (
          <ol className="relative ml-2 border-l-2 border-slate-200" data-testid="dienstverband-timeline">
            {events.map((ev) => {
              const meta = EVENT_META[ev.type] || { label: ev.type, icon: Briefcase, badge: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' };
              const Icon = meta.icon;
              return (
                <li key={ev.id} className="mb-6 ml-6 last:mb-0" data-testid={`dienstverband-event-${ev.id}`}>
                  <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white ${meta.dot}`} />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${meta.badge}`}>
                          <Icon size={12} /> {meta.label}
                        </span>
                        <span className="text-sm font-semibold text-slate-900 tabular-nums">{formatDateNL(ev.effective_date)}</span>
                        {ev.derived && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">afgeleid</span>}
                      </div>
                      {ev.reason && (
                        <p className="mt-1 text-xs text-slate-600">Reden: {UIT_REASON_LABEL[ev.reason] || ev.reason}</p>
                      )}
                      {ev.notes && <p className="mt-0.5 text-xs text-slate-500 italic">{ev.notes}</p>}
                      <p className="mt-1 text-[11px] text-slate-400">
                        Geregistreerd door {ev.created_by_name || 'Systeem'}
                      </p>
                    </div>
                    {canEdit && !ev.derived && ev.id === lastId && (
                      <button
                        onClick={() => removeEvent(ev)}
                        data-testid={`btn-delete-event-${ev.id}`}
                        title="Meest recente gebeurtenis verwijderen"
                        className="inline-flex rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}


const VERZUIM_REASONS = [
  ['', '— Geen categorie —'],
  ['griep', 'Griep / verkoudheid'],
  ['blessure', 'Blessure'],
  ['operatie', 'Operatie / ingreep'],
  ['langdurig', 'Langdurig verzuim'],
  ['anders', 'Anders (zie notitie)'],
];
const VERZUIM_REASON_LABEL = Object.fromEntries(VERZUIM_REASONS.map(([v, l]) => [v, l]));

function daysBetween(start, end) {
  const a = new Date(start);
  const b = new Date(end);
  if (isNaN(a) || isNaN(b)) return null;
  return Math.floor((b - a) / 86400000) + 1; // inclusive
}

function VerzuimTab({ employeeId, canEdit }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [recoverFor, setRecoverFor] = useState(null); // record being marked hersteld
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ start_date: today, reason: '', notes: '' });
  const [recoverDate, setRecoverDate] = useState(today);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/verzuim`);
      setRecords(data.records || []);
    } catch {
      setFeedback({ type: 'error', msg: 'Kon verzuim niet laden' });
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ start_date: today, reason: '', notes: '' }); setFeedback({ type: '', msg: '' }); setShowForm(true); };

  const submitNew = async () => {
    if (!form.start_date) { setFeedback({ type: 'error', msg: 'Vul een startdatum in' }); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/verzuim`, {
        start_date: form.start_date, reason: form.reason || null, notes: form.notes || null,
      });
      setShowForm(false);
      setFeedback({ type: 'success', msg: 'Ziekmelding geregistreerd' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  const submitRecover = async (rec) => {
    if (!recoverDate) { setFeedback({ type: 'error', msg: 'Vul een hersteldatum in' }); return; }
    setSaving(true);
    try {
      await api.put(`/employees/${employeeId}/verzuim/${rec.id}`,
        { status: 'hersteld', end_date: recoverDate },
        { headers: { 'If-Match': `"${rec.id}-${rec.version ?? 0}"` } });
      setRecoverFor(null);
      setFeedback({ type: 'success', msg: 'Hersteld gemeld' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 || e.response?.status === 412) { await load(); setFeedback({ type: 'error', msg: 'Vernieuw: verzuim is gewijzigd' }); }
      else setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Verzuimmelding (${formatDateNL(rec.start_date)}) verwijderen?`)) return;
    try {
      await api.delete(`/employees/${employeeId}/verzuim/${rec.id}`, { headers: { 'If-Match': `"${rec.id}-${rec.version ?? 0}"` } });
      setFeedback({ type: 'success', msg: 'Verzuim verwijderd' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof d === 'string' ? d : 'Verwijderen mislukt' });
    }
  };

  const openCount = records.filter((r) => r.status === 'ziek').length;

  return (
    <div className="space-y-4" data-testid="verzuim-tab">
      {feedback.msg && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
          data-testid="verzuim-feedback"
        >
          {feedback.msg}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Stethoscope size={18} /> Verzuim
            </h3>
            <p className="mt-1 text-sm text-slate-600" data-testid="verzuim-open-count">
              {openCount > 0
                ? <>Momenteel <strong className="text-rose-600">ziek</strong> ({openCount} lopende melding{openCount > 1 ? 'en' : ''})</>
                : 'Geen lopende ziekmelding'}
            </p>
          </div>
          {canEdit && !showForm && (
            <button
              onClick={openNew}
              data-testid="btn-ziekmelden"
              className="flex items-center gap-1.5 rounded-md border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
            >
              <Plus size={14} /> Ziekmelden
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Operationele verzuimregistratie — los van de loonperiode. Voor uitbetaling: gebruik de tab <strong>Verlof &amp; Saldi</strong> (type Ziek).
        </p>
      </div>

      {showForm && canEdit && (
        <div className="rounded-xl border-2 border-brand-amber/30 bg-brand-amber/5 p-5" data-testid="verzuim-form">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Nieuwe ziekmelding</h4>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Eerste ziektedag" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="verzuim-start" />
            <SelectField label="Categorie" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} options={VERZUIM_REASONS} testid="verzuim-reason" />
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notitie (optioneel)</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="verzuim-notes"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
            <button onClick={submitNew} disabled={saving} data-testid="btn-save-verzuim"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Registreren
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : records.length === 0 ? (
          <div className="p-10 text-center" data-testid="verzuim-empty">
            <Stethoscope size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Nog geen verzuim geregistreerd</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Eerste ziektedag</th>
                  <th className="py-2 px-3">Hersteldatum</th>
                  <th className="py-2 px-3 text-right">Duur</th>
                  <th className="py-2 px-3">Categorie</th>
                  <th className="py-2 px-3">Geregistreerd door</th>
                  {canEdit && <th className="py-2 px-3 text-right">Acties</th>}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => {
                  const isOpen = rec.status === 'ziek';
                  const dur = daysBetween(rec.start_date, rec.end_date || today);
                  return (
                    <tr key={rec.id} data-testid={`verzuim-row-${rec.id}`} className="border-b border-slate-100 align-top">
                      <td className="py-2 px-3">
                        <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${isOpen ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {isOpen ? 'Ziek' : 'Hersteld'}
                        </span>
                      </td>
                      <td className="py-2 px-3 tabular-nums text-slate-700">{formatDateNL(rec.start_date)}</td>
                      <td className="py-2 px-3 tabular-nums text-slate-700">{rec.end_date ? formatDateNL(rec.end_date) : '—'}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-600">{dur != null ? `${dur} d${isOpen ? ' (lopend)' : ''}` : '—'}</td>
                      <td className="py-2 px-3 text-slate-600">
                        {VERZUIM_REASON_LABEL[rec.reason] || rec.reason || '—'}
                        {rec.notes && <div className="text-[11px] text-slate-400 italic">{rec.notes}</div>}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-500">{rec.created_by_name || '—'}</td>
                      {canEdit && (
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {isOpen && recoverFor === rec.id ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <input type="date" value={recoverDate} onChange={(e) => setRecoverDate(e.target.value)} data-testid={`verzuim-recover-date-${rec.id}`}
                                className="rounded-md border border-slate-300 px-2 py-1 text-xs" />
                              <button onClick={() => submitRecover(rec)} disabled={saving} data-testid={`btn-confirm-hersteld-${rec.id}`}
                                className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700">OK</button>
                              <button onClick={() => setRecoverFor(null)} className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50">X</button>
                            </div>
                          ) : (
                            <>
                              {isOpen && (
                                <button onClick={() => { setRecoverFor(rec.id); setRecoverDate(today); }} data-testid={`btn-hersteld-${rec.id}`}
                                  className="mr-1 inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50">
                                  <CheckCircle2 size={13} /> Hersteld melden
                                </button>
                              )}
                              <button onClick={() => remove(rec)} data-testid={`btn-delete-verzuim-${rec.id}`}
                                className="inline-flex rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
